import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Select, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { listOrganizations, listVisits, type ListParams } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import { VisitStatus } from '../../types/domain';
import type { Visit } from '../../types/api';

/** Visit-type options for the QA queue. `undefined` = every visit type. */
const QA_VISIT_TYPE_FILTERS = [
    { value: 'all', labelKey: 'common.all' },
    { value: 'monitoring', labelKey: 'visitTypes.monitoring' },
] as const;

/**
 * QA review queue: visits awaiting an approval decision. Deliberately scoped to
 * `pending_approval` only — the Approve/Reject/Flag actions live on the visit
 * detail page and apply solely to visits in this state.
 */
export function QaQueuePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [visitTypeFilter, setVisitTypeFilter] = useState<string>('monitoring');
    const lookups = useAdminLookups({ includeCases: true });

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });

    const visitsQ = useQuery({
        queryKey: ['qa-queue', orgFilter, visitTypeFilter, page],
        queryFn: () =>
            listVisits({
                page,
                limit: DEFAULT_PAGE_SIZE,
                status: VisitStatus.PendingApproval,
                sort: JSON.stringify([{ field: 'completedAt', order: 'DESC' }]),
                ...(orgFilter ? { organizationId: orgFilter } : {}),
                ...(visitTypeFilter !== 'all' ? { visitType: visitTypeFilter } : {}),
            } satisfies ListParams),
    });

    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    const rows = visitsQ.data?.data ?? [];

    const columns = useMemo(
        () => [
            {
                key: 'case',
                header: t('fields.case'),
                render: (v: Visit) => {
                    const caseInfo = lookups.resolveCase(v.caseId, v.caseRecord);
                    return (
                        <EntityRef
                            title={caseInfo.title}
                            id={v.caseId}
                            href={`/cases/${v.caseId}`}
                        />
                    );
                },
            },
            {
                key: 'visitType',
                header: t('fields.visitType'),
                render: (v: Visit) =>
                    v.visitType
                        ? t(`visitTypes.${v.visitType}`, { defaultValue: v.visitType })
                        : t('common.empty'),
            },
            {
                key: 'officer',
                header: t('fields.fieldOfficer'),
                render: (v: Visit) => {
                    const officer = lookups.resolveUser(v.fieldOfficerId, v.fieldOfficer);
                    return (
                        <EntityRef
                            title={officer.title}
                            id={officer.id}
                            secondary={officer.secondary}
                            hideId={!v.fieldOfficerId}
                        />
                    );
                },
            },
            {
                key: 'submitted',
                header: t('fields.submitted'),
                render: (v: Visit) =>
                    v.completedAt
                        ? new Date(v.completedAt).toLocaleString()
                        : t('common.empty'),
            },
            {
                key: 'status',
                header: t('fields.status'),
                render: () => (
                    <Badge variant="light" color="yellow">
                        {t(`visitStatuses.${VisitStatus.PendingApproval}`)}
                    </Badge>
                ),
            },
        ],
        [t, lookups],
    );

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.qaQueue.title')}
                description={t('features.qaQueue.description')}
            />
            <DataPanel>
                <Select
                    label={t('fields.visitType')}
                    placeholder={t('common.all')}
                    clearable
                    searchable
                    maw={360}
                    data={QA_VISIT_TYPE_FILTERS.map((f) => ({
                        value: f.value,
                        label: t(f.labelKey),
                    }))}
                    value={visitTypeFilter === 'all' ? null : visitTypeFilter}
                    onChange={(v) => {
                        setVisitTypeFilter(v ?? 'all');
                        setPage(1);
                    }}
                />
                <Select
                    label={t('fields.organization')}
                    placeholder={t('common.all')}
                    clearable
                    searchable
                    maw={360}
                    data={orgOptions}
                    value={orgFilter}
                    onChange={(v) => {
                        setOrgFilter(v);
                        setPage(1);
                    }}
                />
            </DataPanel>
            <PaginatedTable
                rowKey={(v: Visit) => v.id}
                columns={columns}
                rows={rows}
                total={visitsQ.data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={visitsQ.isLoading}
                onRowClick={(v) => navigate(`/visits/${v.id}`)}
                emptyMessage={t('features.qaQueue.empty')}
            />
        </Stack>
    );
}
